/**
 * Wpis historii pracy dotyczący blokad podopiecznego → klucz tłumaczenia.
 *
 * `restrictions` to lista czynności ZABRONIONYCH podopiecznemu (patrz
 * models/patient-restrictions.model). Kod akcji nadaje backend (poza tym repo)
 * i w tłumaczeniach go nie było, więc w kolumnie „Akcja" stał surowy
 * `edit_restrictions`, a w starej tabeli — sam klucz `WORK-HISTORY.edit_restrictions`,
 * bo ngx-translate oddaje klucz, gdy tłumaczenia brak (błąd 423). Całej rodziny nie
 * znamy — backend potwierdził dwa kody (`edit_patients_restrictions`,
 * `toggle_patients_alarm_phones`) — dlatego rozpoznajemy ją po fragmencie nazwy
 * i składamy opis z czasownika, tak samo jak przy strefach alarmowych.
 *
 * Czyste funkcje, bez zależności od Angulara — testują się bez TestBed.
 */

/** Kolejność bez znaczenia: wpis niesie tylko jeden czasownik. */
const VERBS: Array<{ verb: string, pattern: RegExp }> = [
  { verb: 'ADD', pattern: /add|create/ },
  { verb: 'EDIT', pattern: /edit|update|change|modify|set/ },
  { verb: 'DELETE', pattern: /delete|remove/ }
];

/** Sama nazwa obszaru — do wpisów bez czytelnego czasownika i do kolumny „Opis". */
export const RESTRICTIONS_LABEL_KEY = 'WORK-HISTORY.RESTRICTIONS';

/** Tekst blokady edycji numerów alarmowych — ten sam, który stoi przy przełączniku
 *  na liście podopiecznych, żeby obie strony aplikacji mówiły o niej tak samo. */
const ALARM_PHONES_EDIT_KEY = 'BACKEND.ALARM_PHONES_EDIT';

/** Kody pojedynczych restrykcji → tłumaczenie. */
const RESTRICTION_CODE_KEYS: { [code: string]: string } = {
  editalarmphones: ALARM_PHONES_EDIT_KEY
};

/**
 * Przełącznik blokady edycji numerów alarmowych. W kodzie nie ma ani słowa „restrict",
 * ani czasownika — kierunek niesie `parameter`. Kod potwierdzony przez backend przy
 * błędzie 349; bez niego wpis szedł do tabeli surowy, tak jak `edit_restrictions`
 * przed błędem 423.
 */
const TOGGLE_ALARM_PHONES_ACTION = 'toggle_patients_alarm_phones';

/**
 * Kierunek przełącznika → czasownik opisu. Starszej pisowni (`enabled`/`disabled`)
 * celowo tu nie ma: backend potwierdził, że w starszych wierszach występuje, ale nie
 * to, czy „włączone" znaczy włączoną blokadę, czy włączoną edycję. Zgadywanie
 * odwróciłoby sens wpisu, więc takie wiersze zostają przy neutralnym opisie.
 */
const TOGGLE_PARAM_VERBS: { [parameter: string]: string } = {
  blocking_on: 'ADD',
  blocking_off: 'DELETE'
};

/**
 * Blokada podopiecznego — w kodzie akcji ALBO w obszarze wpisu. Oba pola naraz, bo
 * backend rozbija część wpisów na czasownik i obszar. Obszarem bywa nazwa pola
 * (`edit` + `restrictions`), ale też sam kod restrykcji (`delete` + `editAlarmPhones`),
 * w którym słowa „restrict" nie ma już wcale — a wygląda wtedy jak zwykła zmiana
 * numerów alarmowych. Parametr jest częścią sygnatury właśnie po to, żeby kolejne
 * wywołanie nie mogło go po cichu pominąć: na tym polegały błędy 415, 417 i 419.
 */
export function isRestrictionAction(action: string, parameter?: string): boolean {
  return isRestrictionTerm(action) || isRestrictionTerm(parameter);
}

/**
 * `null` = wpis nie dotyczy blokad; wtedy wywołujący zostaje przy swoim
 * dotychczasowym zachowaniu.
 */
export function toRestrictionActionKey(action: string, parameter?: string): string {
  if (!isRestrictionAction(action, parameter)) {
    return null;
  }
  if (normalize(action) === 'edit_patients_restrictions') {
    return 'WORK-HISTORY.RESTRICTIONS-EDIT-ALL';
  }
  const verb = toToggleVerb(action, parameter) || toVerb(action) || toVerb(parameter);
  return verb ? 'WORK-HISTORY.RESTRICTIONS-' + verb : RESTRICTIONS_LABEL_KEY;
}

/**
 * Pojedyncze słowo z opisu wpisu (nazwa pola albo kod restrykcji) → klucz tłumaczenia.
 * Kolumna „Opis" pokazuje surowe pola JSON-a z backendu; tutaj nazywamy te, które front
 * zna. `null` = zostaje tak, jak przyszło.
 */
export function toRestrictionTermKey(text: string): string {
  const term = normalize(text);
  if (!term) {
    return null;
  }
  if (RESTRICTION_CODE_KEYS[term]) {
    return RESTRICTION_CODE_KEYS[term];
  }
  // Kolumna „Szczegóły" pokazuje `parameter`, a przy przełączniku stoi w nim kierunek.
  // Kierunek mówi już etykieta akcji, więc tutaj nazywamy samą blokadę — tym samym
  // tekstem co przy `editAlarmPhones`, żeby obie strony aplikacji mówiły tak samo.
  if (TOGGLE_PARAM_VERBS[term]) {
    return ALARM_PHONES_EDIT_KEY;
  }
  return term === 'restrictions' || term === 'restriction' ? RESTRICTIONS_LABEL_KEY : null;
}

/**
 * Nazwa pola po fragmencie, kod restrykcji dokładnie. Dopasowanie kodu po fragmencie
 * zabrałoby ikonę i opis zwykłej zmianie numerów alarmowych (`edit_alarm_phones`),
 * bo `editAlarmPhones` różni się od niej tylko podkreślnikami.
 */
function isRestrictionTerm(value: string): boolean {
  const term = normalize(value);
  return !!term && (term.indexOf('restrict') !== -1 || term === TOGGLE_ALARM_PHONES_ACTION
    || !!RESTRICTION_CODE_KEYS[term]);
}

/**
 * Czasownik przełącznika blokady — w samym kodzie akcji go nie ma, stoi w parametrze.
 * `null` przy nieznanym kierunku, więc wywołujący spada na neutralny opis.
 */
function toToggleVerb(action: string, parameter: string): string {
  return normalize(action) === TOGGLE_ALARM_PHONES_ACTION
    ? TOGGLE_PARAM_VERBS[normalize(parameter)] || null
    : null;
}

function toVerb(value: string): string {
  const matched = VERBS.filter(candidate => candidate.pattern.test(normalize(value)))[0];
  return matched ? matched.verb : null;
}

function normalize(value: string): string {
  return (value || '').toString().trim().toLowerCase();
}
